/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TESTCYCLE_STATUS, TEST_CYCLE_STATUS_STRINGS } from '../../../../constants';

export type ProjectRowKind = 'project' | 'invitation';

interface RowView {
  icon: string;
  tone: 'active' | 'pending' | 'invitation';
  status?: string;
  recruited?: boolean;
  survey?: boolean;
  name: string;
  href: string;
  testers?: number;
  bugs?: number;
  start?: number;
  end?: number;
  // Locks shows "in seconds" once the end date has passed on an active cycle.
  hidePriorLock?: boolean;
  showLock: boolean;
}

// One row of the My Activity card's Projects or Invitations tab: a test cycle, a test cycle invitation or a
// requirement survey invitation.
@Component({
  selector: 'ut-dashboard-project-row',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dashboard-project-row.component.html',
  styleUrls: ['./dashboard-project-row.component.scss'],
})
export class DashboardProjectRowComponent {
  @Input({ required: true }) item: any;
  @Input({ required: true }) kind: ProjectRowKind;
  @Input() testerAppUrl = '';

  get view(): RowView {
    const { item, testerAppUrl } = this;
    if (this.kind === 'project') {
      const active = item.testCycleStatusId === TESTCYCLE_STATUS.ACTIVE;
      return {
        icon: 'assignment',
        tone: active ? 'active' : 'pending',
        status: item.testCycleStatusDescriptor?.name,
        name: item.name,
        href: `${testerAppUrl}/testcycles/${item.id}/`,
        testers: item.numberOfTesters,
        bugs: item.totalBugs,
        start: item.activationDate || item.startDate,
        end: item.deactivationDate,
        hidePriorLock: active,
        showLock: true,
      };
    }
    if (item.action === 'requirement_survey') {
      return {
        icon: 'email',
        tone: 'invitation',
        survey: true,
        name: item.name,
        href: `${testerAppUrl}/requirement-survey/${item.requirementSurveyId}`,
        end: item.endDate,
        showLock: false,
      };
    }
    const cycle = item.testcycle || {};
    return {
      icon: 'email',
      tone: 'invitation',
      recruited: item.recruited,
      name: cycle.name,
      href: `${testerAppUrl}/testcycles/${cycle.id}/`,
      testers: cycle.participants,
      // The bug count is withheld from cycles that require MFA.
      bugs: item.mfaRequired ? undefined : cycle.totalBugs,
      start: cycle.startDate,
      end: cycle.endDate,
      hidePriorLock: cycle.testCycleStatusId === TEST_CYCLE_STATUS_STRINGS.active,
      showLock: true,
    };
  }
}
